import amplitude from 'amplitude-js';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import captureSpecificError from '../sentry';
import { telemetry } from '../backend';
import { getStore } from '../store';
import { getOnTheWaterFromState } from '../store/modules/signal';
import { selectHasCurrentMemberActiveSubscription } from '../store/modules/backend-service';

let amplitudeInitialised = false;

const buildAdditionalInformation = async () => {
  const state = getStore().getState();
  const { currentMember } = state.backendService;

  const clientOnTheWaterState = getOnTheWaterFromState(state);

  // eslint-disable-next-line no-undef

  const searchParams = {};
  if (window.appHistory) {
    const urlParams = new URLSearchParams(window.appHistory?.location?.search);

    urlParams.forEach((v, k) => {
      searchParams[`qs_${k}`] = v;
    });
  }

  const info = await Device.getInfo();
  const subscribed = selectHasCurrentMemberActiveSubscription(state);

  return {
    model: info.model,
    platform: info.platform ?? 'Browser',
    version: info.osVersion,
    device: info.name,
    manufacturer: info.manufacturer,
    isVirtual: info.isVirtual, // Not sure if we want this? Would be VERY rare if its virtual
    environment: Capacitor.isNativePlatform() ? 'capacitor' : 'browser',
    /* eslint-enable no-undef */
    isLoggedIn: !!currentMember,
    clientOnTheWaterState,
    subscribed,
    ...searchParams,
  };
};

export const captureEvent = async ({ type, payload, sendToAmplitude }) => {
  const additionalInformation = await buildAdditionalInformation();
  const { userProperties, ...rest } = payload;
  if (amplitudeInitialised && sendToAmplitude) {
    amplitude
      .getInstance()
      .logEvent(type, { ...additionalInformation, ...rest });

    if (userProperties) {
      userProperties.forEach((property) => {
        switch (property.type) {
          default:
          case 'set':
            amplitude
              .getInstance()
              .identify(
                new amplitude.Identify().set(property.name, property.value),
              );
            break;
          case 'append':
            amplitude
              .getInstance()
              .identify(
                new amplitude.Identify().append(property.name, [
                  property.value,
                ]),
              );
            break;
          case 'increment':
            amplitude
              .getInstance()
              .identify(new amplitude.Identify().add(property.name, 1));
            break;
          case 'decrement':
            amplitude
              .getInstance()
              .identify(new amplitude.Identify().add(property.name, -1));
            break;
        }
      });
    }
  }
};

/**
 * Format user properties if changing, ensure changes are reflected in each implementation
 * @param {array} watercrafts - User property
 * @param {array} interests - User property
 * @param {array} trips - User property
 * @param {array} floatPlans - User property
 * @return {object} Object containing user properties to be recorded
 */
const formatUserProperties = ({
  watercrafts,
  interests,
  trips,
  floatPlans,
  otherInterest,
}) => {
  const userInterests = interests?.map((interest) => interest.name) ?? [];
  if (otherInterest) {
    userInterests.push(otherInterest);
  }

  const state = getStore().getState();
  const subscribed = selectHasCurrentMemberActiveSubscription(state);

  return {
    interests: userInterests,
    tripCount: trips?.length ?? undefined,
    floatPlanCount: floatPlans?.length ?? undefined,
    watercraftCount: watercrafts?.length ?? undefined,
    subscribed,
    watercraft:
      watercrafts?.map((watercraft) => ({
        type: watercraft.watercraft_type,
        make_model: watercraft.make_model,
        length: watercraft.vessel_length
          ? `${watercraft.vessel_length}${watercraft.length_units}`
          : null,
        beam: watercraft.beam
          ? `${watercraft.beam}${watercraft.beam_units}`
          : null,
        draft: watercraft.draft
          ? `${watercraft.draft}${watercraft.draft_units}`
          : null,
        capacity: watercraft.capacity,
      })) ?? [],
  };
};

let identifyRetryQueue = [];

const identifyAmplitude = (identity, queueOnFail) => {
  if (amplitudeInitialised) {
    if (identity) {
      const { id } = identity;
      const userProperties = formatUserProperties(identity);

      amplitude.getInstance().setUserId(id);
      amplitude.getInstance().setUserProperties(userProperties);
    } else {
      amplitude.getInstance().setUserId(null);
    }
  } else if (queueOnFail) {
    identifyRetryQueue.push(identity);
  }
};

export const identify = (identity) => identifyAmplitude(identity, true);

export const initialize = async () => {
  const amplitudeApiKey = window.DECKEE_CONFIG?.amplitude?.apiKey ?? null;
  if (amplitudeApiKey) {
    amplitude.getInstance().init(amplitudeApiKey);
    amplitudeInitialised = true;

    // Now that we're initialised, identify any queued identities.

    identifyRetryQueue?.forEach((identity) =>
      identifyAmplitude(identity, false),
    );
    identifyRetryQueue = [];
  } else {
    const notificationMessage =
      "[amplitude.initialise] failed initialise amplitude: couldn't retrieve api key";
    /* eslint-disable no-bitwise */
    telemetry(
      telemetry.WARN | telemetry.NOTIFY_SYSTEM_CHANNEL,
      notificationMessage,
    );
    /* eslint-enable no-bitwise */
    captureSpecificError(
      notificationMessage,
      'containers/app-main-client/src/services/event-capture/amplitude.js',
    );
  }
};
